// LUCKY DUCKY · PHASE 2 · Reel engine + Cardinal evaluator
// Pure functions.  UI-agnostic.  Same code powers both the playable game AND
// the developer simulator (see engine/simulator.js).
//
// Evaluation rules (from verified_paytable.json + Phase 2 message):
//   · Standard lines evaluate left-to-right starting on Reel 1. Gaps break.
//   · WILD substitutes for all symbols except BONUS.
//   · 1 or 2 Wilds on a winning payline pay the standard win × 2.
//   · 3/4/5 Wilds forming their OWN consecutive line use DOUBLE_WILD_TABLE.
//   · Mixed line with ≥3 Wilds + ≥1 standard symbol → NEEDS_VERIFICATION,
//     pays 0 credits and is COUNTED in telemetry for later Cardinal review.
//   · BONUS is scatter (evaluated over entire 5×3 grid), pays per BONUS_SCATTER_TABLE.
//   · During BETO RESPINS feature, ALL non-scatter wins are multiplied by 3.
import { REEL_STRIPS, TOTAL_STOPS_PER_REEL } from './reelStrips';
import { PAYLINES_20, REEL_COUNT, ROW_COUNT } from './paylines';
import { STANDARD_LINE_PAYS, DOUBLE_WILD_TABLE, BONUS_SCATTER_TABLE, RULES } from './paytable';
import { SYMBOL_IDS } from './symbols';

/** Symbol keys in paytable are UPPERCASE canonical names — map engine IDs. */
const PAY_KEY = {
  CHERRY:   'CHERRIES',
  ORANGE:   'ORANGE',
  WATERMEL: 'WATERMELON',
  PLUM:     'PLUM',
  BELL:     'BELL',
  B1R:      'B1R',
  B2R:      'B2R',
  B3R:      'B3R',
  RED_7:    'RED_7',
};

/** Draw one 5x3 window; grid[reel][row], row 0 = top. */
export function spinGrid(rng = Math.random) {
  return REEL_STRIPS.map(strip => {
    const stop = Math.floor(rng() * TOTAL_STOPS_PER_REEL);
    return [
      strip[(stop) % TOTAL_STOPS_PER_REEL],
      strip[(stop + 1) % TOTAL_STOPS_PER_REEL],
      strip[(stop + 2) % TOTAL_STOPS_PER_REEL],
    ];
  });
}

/** Count BONUS symbols anywhere on the visible 5×3 window (scatter). */
export function countBonusOnGrid(grid) {
  let n = 0;
  for (let r = 0; r < REEL_COUNT; r++) {
    for (let row = 0; row < ROW_COUNT; row++) {
      if (grid[r][row] === 'BONUS') n++;
    }
  }
  return n;
}

/** Evaluate one line.  Returns { line, kind, symbol, hits, pay, flagged } or null. */
function evalLine(grid, line) {
  const seq = line.rows.map((row, r) => grid[r][row]);

  // BONUS never contributes to line pays.
  if (seq[0] === 'BONUS') return null;

  // Count leading matches under standard rules with WILD substitution.
  //  · Determine "anchor" = first non-WILD, non-BONUS symbol L→R.
  //    If the anchor never appears (all leading positions are WILD/BONUS),
  //    treat this as a Wild-only sequence and dispatch to the standalone
  //    Wild evaluator.
  let anchor = null;
  let wildLead = 0;
  for (let i = 0; i < seq.length; i++) {
    if (seq[i] === 'WILD') { wildLead++; continue; }
    if (seq[i] === 'BONUS') { break; }
    anchor = seq[i];
    break;
  }

  // ── Case A: no standard anchor found among leading positions.
  //    Sequence begins with wilds (or entirely wilds).  Count leading wilds
  //    only — that's the standalone Wild line evaluation.
  if (anchor === null) {
    let hits = 0;
    for (let i = 0; i < seq.length; i++) {
      if (seq[i] === 'WILD') hits++;
      else break;
    }
    if (hits < 3) return null;
    const pay = DOUBLE_WILD_TABLE[String(hits)];
    if (!pay) return null;
    return { line: line.id, kind: 'wild-standalone', symbol: 'WILD', hits, pay, flagged: false };
  }

  // ── Case B: mixed line with an anchor + possible wilds.
  //    Count consecutive matches where a match = anchor OR wild.  Gap breaks.
  let hits = 0;
  let wildsInWin = 0;
  for (let i = 0; i < seq.length; i++) {
    const s = seq[i];
    if (s === anchor)      { hits++; continue; }
    if (s === 'WILD')      { hits++; wildsInWin++; continue; }
    break;
  }

  // Special-case: line begins with wilds, then anchor appears.  Recount wilds
  // participating in the winning stretch (includes lead wilds).  Already done
  // above because we included leading wilds in the anchor-match count via the
  // `s === 'WILD'` branch — good.

  if (hits < 3) return null;

  const payKey = PAY_KEY[anchor];
  const tier = STANDARD_LINE_PAYS[payKey];
  if (!tier || !tier[String(hits)]) return null;

  // Mixed 3+ Wilds + standard = NEEDS_VERIFICATION → pay 0, flag telemetry.
  if (wildsInWin >= 3) {
    return { line: line.id, kind: 'mixed-3plus-wild-flagged', symbol: anchor, hits, pay: 0, flagged: true };
  }

  let pay = tier[String(hits)];
  // 1 or 2 Wilds on a winning line → line pay × 2 (Cardinal-locked).
  if (wildsInWin >= 1 && wildsInWin <= 2) pay *= RULES.wild_line_multiplier_for_1_or_2;

  return { line: line.id, kind: wildsInWin ? 'wild-assisted' : 'standard', symbol: anchor, hits, pay, flagged: false };
}

/**
 * Full grid evaluation.
 * @param {number[][]} grid 5×3 grid[reel][row]
 * @param {boolean} isFeature If true, ALL non-scatter wins × RULES.feature_win_multiplier.
 * @returns {object} full evaluation result
 */
export function evaluateGrid(grid, isFeature = false) {
  const lineWins = [];
  let flaggedMixed = 0;
  PAYLINES_20.forEach(line => {
    const w = evalLine(grid, line);
    if (!w) return;
    if (w.flagged) flaggedMixed++;
    lineWins.push(w);
  });

  const bonusCount = countBonusOnGrid(grid);
  const bonusScatterPay = BONUS_SCATTER_TABLE[String(bonusCount)] || 0;

  const lineTotal = lineWins.reduce((s, w) => s + w.pay, 0);
  const featureMult = isFeature ? RULES.feature_win_multiplier : 1;
  const totalWin = (lineTotal * featureMult) + bonusScatterPay; // scatter is NOT multiplied by feature per typical scatter norms — see NEEDS_VERIFICATION

  return {
    grid,
    lineWins,
    lineTotal,
    bonusCount,
    bonusScatterPay,
    featureMultiplierApplied: featureMult,
    totalWin,
    // Cardinal state flags for the caller
    enters_respin:      bonusCount >= RULES.min_bonus_to_enter_respin,      // 2+
    enters_free_plays:  bonusCount >= RULES.min_bonus_to_award_free_plays,  // 3+
    free_plays_awarded: bonusCount >= RULES.min_bonus_to_award_free_plays ? RULES.free_plays_awarded_on_trigger : 0,
    // Telemetry for the simulator
    flags: {
      mixed_3plus_wild_flagged: flaggedMixed,      // NEEDS_VERIFICATION count
    },
  };
}

export { REEL_COUNT, ROW_COUNT, PAYLINES_20, SYMBOL_IDS };
